<script lang="ts">
	import Countdown from "@components/Countdown.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { getTranslate } from "@utils/translate.js";
	import { onMount } from "svelte";
	import { toDateMonthYearFormat } from "@core/utils/toDateMonthYearFormat.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./Homepage.translations.json" with { type: "json" };
	import type { HomepageProps } from "./HomepageProps.js";

	const { activeCompetitions, upcomingCompetitions, pastCompetitions }: HomepageProps = $props();
	const { tenant } = getGlobalTemplateProps();
	const { WEB_URL } = getGlobalSettings();
	const translate = getTranslate(translations);

	const firstActive = $derived(activeCompetitions[0]);

	// Live "now" for the registration progress bars (the countdowns tick themselves).
	let now = $state(new Date());
	onMount(() => {
		const interval = setInterval(() => (now = new Date()), 30_000);
		return (): void => {
			clearInterval(interval);
		};
	});
	function registrationProgress(start: string, end: string): number {
		const from = new Date(start).getTime();
		const to = new Date(end).getTime();
		if (to <= from) {
			return 100;
		}
		return Math.min(100, Math.max(0, ((now.getTime() - from) / (to - from)) * 100));
	}

	// Lucide icons (the FlyFetch design system's icon set) — inlined as raw SVG.
	const iconAttrs =
		'xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';
	const mapPinIcon = `<svg ${iconAttrs}><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>`;
	const calendarIcon = `<svg ${iconAttrs}><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/></svg>`;
	const arrowRightIcon = `<svg ${iconAttrs}><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>`;

	const structuredDataJson = $derived(
		JSON.stringify({
			"@context": "https://schema.org",
			"@type": "ItemList",
			name: translate("headline"),
			description: translate("intro"),
			itemListElement: [...activeCompetitions, ...upcomingCompetitions, ...pastCompetitions].map((item, index) => ({
				"@type": "ListItem",
				position: index + 1,
				item: {
					"@type": "SportsEvent",
					name: item.competition.headline[tenant.locale],
					description: item.competition.description[tenant.locale],
					startDate: item.competition.competitionStart,
					endDate: item.competition.competitionEnd,
					location: {
						"@type": "Place",
						name: item.competition.location[tenant.locale],
					},
					url: `${WEB_URL}${item.applicationPath}`,
				},
			})),
		}),
	);

	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

{#snippet heroStat(value: number, label: string, href: string)}
	<a {href} class="group flex flex-col transition-colors">
		<span
			class="tabular text-[2.5rem] font-bold leading-none text-white transition-colors group-hover:text-secondary-200"
		>
			{value}
		</span>
		<span class="eyebrow mt-2 text-sand-300 transition-colors group-hover:text-white">{label}</span>
	</a>
{/snippet}

{#snippet sectionHead(accent: string, title: string, count: number)}
	<div class="mb-6 flex items-baseline gap-3">
		<span class={["h-7 w-1.5 shrink-0 self-center rounded-full", accent]} aria-hidden="true"></span>
		<h2 class="!mb-0">{title}</h2>
		<span class="tabular text-ink-400 text-lg">{count}</span>
	</div>
{/snippet}

<svelte:head>
	<svelte:element this={"script"} type="application/ld+json">{structuredDataJson}</svelte:element>
</svelte:head>

<Layout>
	<!-- Hero — dark ink panel with a red→ink wash and a disc motif -->
	<section
		class="bg-ink-950 relative mt-2 mb-12 overflow-hidden rounded-[20px] px-5 py-10 text-white shadow-elevated sm:mb-14 sm:rounded-[22px] sm:px-12 sm:py-16 lg:px-16 lg:py-20"
	>
		<div
			class="pointer-events-none absolute inset-0"
			style="background: linear-gradient(135deg, rgba(56,113,193,0.36) 0%, rgba(56,113,193,0.07) 36%, rgba(32,31,31,0) 64%);"
			aria-hidden="true"
		></div>
		<div class="pointer-events-none absolute inset-0" aria-hidden="true">
			<svg
				class="absolute -right-16 -top-20 h-[360px] w-[360px] text-white/[0.06] sm:-right-8"
				viewBox="0 0 100 100"
				fill="none"
				stroke="currentColor"
				stroke-width="1.2"
			>
				<circle cx="50" cy="50" r="48" /><circle cx="50" cy="50" r="33" /><circle cx="50" cy="50" r="6" />
			</svg>
			<svg
				class="absolute -bottom-24 -left-16 h-56 w-56 text-secondary/20"
				viewBox="0 0 100 100"
				fill="none"
				stroke="currentColor"
				stroke-width="1.2"
			>
				<circle cx="50" cy="50" r="48" /><circle cx="50" cy="50" r="33" />
			</svg>
		</div>

		<div class="relative flex max-w-3xl flex-col gap-5 sm:gap-6">
			<p class="eyebrow ff-fade-up text-sand-300">{translate("operatorEyebrow")}</p>
			<h1 class="ff-fade-up ff-d1 !mb-0 !text-white !leading-[0.92] !text-[clamp(2.25rem,7vw,4.75rem)]">
				{translate("headline")}
			</h1>
			<p class="ff-fade-up ff-d2 text-ink-300 max-w-2xl text-base leading-normal sm:text-md xl:text-lg">
				{translate("intro")}
			</p>

			<div class="ff-fade-up ff-d3 mt-1 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
				{#if firstActive}
					<Link
						href={firstActive.applicationPath}
						class="bg-secondary shadow-action ease-spring hover:bg-secondary-600 inline-flex items-center justify-center gap-2 rounded-md px-7 py-3.5 font-semibold tracking-[0.02em] text-white transition duration-150 hover:-translate-y-px active:scale-[0.97]"
					>
						{translate("ctaApply")}
						<Icon icon={arrowRightIcon} class="h-4 w-4" />
					</Link>
				{/if}
				<a
					href="#active"
					class="ease-spring inline-flex items-center justify-center gap-2 rounded-md border-2 border-white/25 px-7 py-3.5 font-semibold tracking-[0.02em] text-white transition duration-150 hover:-translate-y-px hover:border-white/50 hover:bg-white/10 active:scale-[0.97]"
				>
					{translate("activeSection")}
				</a>
			</div>

			<div
				class="ff-fade-up ff-d4 mt-5 flex flex-wrap gap-x-10 gap-y-5 border-t border-white/10 pt-6 sm:mt-6 sm:gap-x-12 sm:pt-7"
			>
				{@render heroStat(activeCompetitions.length, translate("activeSection"), "#active")}
				{@render heroStat(upcomingCompetitions.length, translate("upcomingSection"), "#upcoming")}
				{@render heroStat(pastCompetitions.length, translate("pastSection"), "#past")}
			</div>
		</div>
	</section>

	<!-- Active competitions -->
	<section id="active" class="mb-14 scroll-mt-24 sm:mb-16 sm:scroll-mt-28">
		{@render sectionHead("bg-secondary", translate("activeSection"), activeCompetitions.length)}
		<div class="grid gap-5 sm:grid-cols-2">
			{#each activeCompetitions as item (item.competition.id)}
				<Link
					href={item.applicationPath}
					class="ease-snappy shadow-card hover:shadow-card-hover border-ink-200 hover:border-secondary/40 group relative flex flex-col gap-3 overflow-hidden rounded-lg border bg-white p-5 transition duration-200 hover:-translate-y-1 sm:p-6"
				>
					<span class="bg-secondary absolute inset-x-0 top-0 h-1.5" aria-hidden="true"></span>
					<span
						class="bg-grass-100 text-grass-600 inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em]"
					>
						<span class="bg-grass-500 h-1.5 w-1.5 rounded-full"></span>{translate("statusOpen")}
					</span>
					<span
						class="font-display text-ink-950 group-hover:text-secondary text-[1.5rem] font-bold uppercase leading-[1.05] tracking-[-0.01em] transition-colors"
					>
						{item.competition.headline[tenant.locale]}
					</span>
					<div class="text-ink-600 flex flex-col gap-1.5 text-sm">
						<span class="flex items-center gap-2">
							<Icon icon={mapPinIcon} class="text-ink-400 h-4 w-4" />
							{item.competition.location[tenant.locale]}
						</span>
						<span class="flex items-center gap-2">
							<Icon icon={calendarIcon} class="text-ink-400 h-4 w-4" />
							<span class="tabular text-ink-700">
								{translate("competitionDateRange", {
									start: toDateMonthYearFormat(item.competition.competitionStart, tenant.locale),
									end: toDateMonthYearFormat(item.competition.competitionEnd, tenant.locale),
								})}
							</span>
						</span>
					</div>
					<div class="border-ink-100 mt-1 flex flex-col gap-2 border-t pt-3">
						<div class="flex items-center justify-between gap-2">
							<span class="eyebrow text-ink-500">{translate("untilRegistrationEnd")}</span>
							<Countdown target={item.competition.registrationEnd} compact />
						</div>
						<div class="bg-ink-200 h-1.5 w-full overflow-hidden rounded-full">
							<div
								class="bg-secondary h-full rounded-full transition-[width] duration-700 ease-out"
								style="width: {registrationProgress(
									item.competition.registrationStart,
									item.competition.registrationEnd,
								)}%"
							></div>
						</div>
					</div>
					<span class="text-secondary mt-1 inline-flex items-center gap-1.5 font-semibold">
						{translate("ctaApply")}
						<Icon icon={arrowRightIcon} class="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
					</span>
				</Link>
			{:else}
				<p
					class="border-ink-300 text-ink-500 rounded-lg border border-dashed bg-white/60 px-5 py-10 text-center text-sm"
				>
					{translate("noActive")}
				</p>
			{/each}
		</div>
	</section>

	<!-- Upcoming competitions -->
	<section id="upcoming" class="mb-14 scroll-mt-24 sm:mb-16 sm:scroll-mt-28">
		{@render sectionHead("bg-amber-500", translate("upcomingSection"), upcomingCompetitions.length)}
		<div class="grid gap-5 sm:grid-cols-2">
			{#each upcomingCompetitions as item (item.competition.id)}
				<article
					class="border-ink-300 relative flex flex-col gap-3 overflow-hidden rounded-lg border border-dashed bg-white p-5 sm:p-6"
				>
					<span class="bg-amber-500 absolute inset-x-0 top-0 h-1.5" aria-hidden="true"></span>
					<span
						class="bg-amber-100 text-ink-700 inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em]"
					>
						<span class="bg-amber-500 h-1.5 w-1.5 rounded-full"></span>{translate("statusComingSoon")}
					</span>
					<span class="font-display text-ink-950 text-[1.5rem] font-bold uppercase leading-[1.05] tracking-[-0.01em]">
						{item.competition.headline[tenant.locale]}
					</span>
					<div class="text-ink-600 flex flex-col gap-1.5 text-sm">
						<span class="flex items-center gap-2">
							<Icon icon={mapPinIcon} class="text-ink-400 h-4 w-4" />
							{item.competition.location[tenant.locale]}
						</span>
						<span class="flex items-center gap-2">
							<Icon icon={calendarIcon} class="text-ink-400 h-4 w-4" />
							<span class="tabular text-ink-700">
								{translate("competitionDateRange", {
									start: toDateMonthYearFormat(item.competition.competitionStart, tenant.locale),
									end: toDateMonthYearFormat(item.competition.competitionEnd, tenant.locale),
								})}
							</span>
						</span>
					</div>
					<div class="border-ink-100 mt-1 flex items-center justify-between gap-2 border-t pt-3">
						<span class="eyebrow text-ink-500">{translate("untilRegistrationStart")}</span>
						<Countdown target={item.competition.registrationStart} compact />
					</div>
				</article>
			{:else}
				<p
					class="border-ink-300 text-ink-500 rounded-lg border border-dashed bg-white/60 px-5 py-10 text-center text-sm"
				>
					{translate("noUpcoming")}
				</p>
			{/each}
		</div>
	</section>

	<!-- Past competitions -->
	<section id="past" class="mb-6 scroll-mt-24 sm:scroll-mt-28">
		{@render sectionHead("bg-ink-400", translate("pastSection"), pastCompetitions.length)}
		<div class="border-ink-200 shadow-card rounded-lg border bg-white p-2">
			<ul class="flex flex-col">
				{#each pastCompetitions as item (item.competition.id)}
					<li
						class="hover:bg-ink-50 flex flex-col gap-2 rounded-md px-4 py-3.5 transition-colors sm:flex-row sm:items-center sm:justify-between"
					>
						<div class="flex flex-col gap-1.5">
							<div class="flex items-center gap-2">
								<span
									class="bg-ink-100 text-ink-600 inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em]"
								>
									{translate("statusFinished")}
								</span>
								<span class="tabular text-ink-500 text-xs">
									{toDateMonthYearFormat(item.competition.competitionStart, tenant.locale)}
								</span>
							</div>
							<span
								class="font-display text-ink-900 text-[1.25rem] font-bold uppercase leading-tight tracking-[-0.01em]"
							>
								{item.competition.headline[tenant.locale]}
							</span>
						</div>
						<Link
							href={item.applicationsPath}
							class="text-secondary hover:text-secondary-600 group inline-flex w-fit items-center gap-1.5 text-sm font-semibold"
						>
							{translate("ctaApplications")}
							<Icon icon={arrowRightIcon} class="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
						</Link>
					</li>
				{:else}
					<li class="text-ink-500 px-4 py-10 text-center text-sm">{translate("noPast")}</li>
				{/each}
			</ul>
		</div>
	</section>
</Layout>
